import { assert } from "@faro-lotv/foundation";
import { MAX_CONCURRENT_UPLOADS } from "@faro-lotv/service-wires";
import { WindowExtractionSequencer } from "./window-extraction-sequencer";

/**
 * Maximum number of ongoing uploads.
 * Set to the double of the number of concurrent uploads to ensure the upload queue remains active and bandwidth stays saturated.
 */
const MAX_ONGOING_UPLOADS = MAX_CONCURRENT_UPLOADS * 2;

/**
 * Number of tiles to upload by process
 * Set to the maximum number of concurrent uploads to optimize each upload process.
 */
export const NB_OF_TILES_TO_UPLOAD_BY_PROCESS = MAX_CONCURRENT_UPLOADS;

/**
 * Maximum number of concurrent processes for tile uploads
 * Set to the minimum number of processes required to reach the MAX_ONGOING_UPLOADS
 * while avoiding overloading the system memory during tile uploads.
 */
const MAX_CONCURRENT_PROCESSES =
  MAX_ONGOING_UPLOADS / NB_OF_TILES_TO_UPLOAD_BY_PROCESS;

/** Statistics for tile uploads */
export type TilesUploadStats = {
  /** Total number of upload processes that have been run.*/
  totalProcessesRan: number;
  /** Current number of ongoing uploads processes.*/
  currentOngoingProcesses: number;
  /** Total number of upload tiles promises created.*/
  nbOfUploadTilesPromises: number;
};

/**
 * Type definition for an object that holds a promise, its resolver function, and a fulfillment status.
 */
export type PredicatedPromiseData = {
  /** The promise being tracked. This will be initialized by the getPredicatedPromise function */
  promise: Promise<void> | null;
  /** The resolver function for the promise. This will be initialized by the getPredicatedPromise function */
  resolver: (() => void) | null;
  /** Whether the promise has been fulfilled */
  fulfilled: boolean;
  /**
   * A predicate function to determine if the promise can be resolved
   *
   * @returns A boolean indicating if the promise can be resolved.
   */
  predicate(): boolean;
};

/**
 * Gets the promise for the given promise data.
 *
 * @param promiseData The promise data object containing the promise, resolver, and predicate.
 * @param onResolve Optional callback to execute when the promise is resolved.
 * @returns A promise that resolves when the predicate condition is met.
 */
export function getPredicatedPromise(
  promiseData: PredicatedPromiseData,
  onResolve?: () => void,
): Promise<void> {
  if (promiseData.promise && !promiseData.fulfilled) {
    return promiseData.promise;
  }

  promiseData.fulfilled = false;
  promiseData.promise = new Promise<void>((resolve) => {
    promiseData.resolver = () => {
      if (!promiseData.fulfilled && promiseData.predicate()) {
        resolve();
        promiseData.fulfilled = true;
        onResolve?.();
      }
    };

    // Resolve immediately if conditions are already met
    promiseData.resolver();
  });

  return promiseData.promise;
}

/**
 * Coordinator for managing the generation and upload of tiles.
 * It avoids overloading the system with too many concurrent uploads
 * and ensures that the app is not consuming too much memory during the process.
 */
export class TilesGenerationCoordinator {
  /** Current number of files being uploaded.*/
  private onGoingUploads: number = 0;

  /** Collection of all the statistics for tile uploads currently running */
  private tilesUploadStatsList: TilesUploadStats[] = [];

  /** Holds the promise and its resolver that resolves when a new upload process can start */
  private startUploadProcess: PredicatedPromiseData = {
    promise: null,
    resolver: null,
    fulfilled: false,
    predicate: () => {
      const totalOngoingProcesses = this.tilesUploadStatsList.reduce(
        (sum, stats) => sum + stats.currentOngoingProcesses,
        0,
      );
      return (
        this.onGoingUploads < MAX_ONGOING_UPLOADS &&
        totalOngoingProcesses < MAX_CONCURRENT_PROCESSES
      );
    },
  };

  /** Holds the promise and its resolver that resolves when the tiles parent canvas can be generated */
  private tilesParentCanvasGeneration: PredicatedPromiseData = {
    promise: null,
    resolver: null,
    fulfilled: false,
    predicate: () =>
      this.onGoingUploads < MAX_ONGOING_UPLOADS &&
      this.windowExtractionSequencer.isPreviousWindowExtractionCompleted(),
  };

  /**
   * Initializes a new instance of the class with the provided WindowExtractionSequencer.
   * Sets up a Proxy for `tilesUploadStats` to intercept and handle updates to upload statistics.
   *
   * @param windowExtractionSequencer - The sequencer responsible for managing window extraction processes.
   */
  constructor(public windowExtractionSequencer: WindowExtractionSequencer) {}

  /**
   * @returns A promise that resolves when a new upload process can start.
   */
  getStartUploadProcessPromise(): Promise<void> {
    return getPredicatedPromise(this.startUploadProcess);
  }

  /**
   * @returns A promise that resolves when the tiles parent canvas can be generated.
   */
  getTilesParentCanvasGenerationPromise(): Promise<void> {
    return getPredicatedPromise(this.tilesParentCanvasGeneration);
  }

  /**
   * Sets the number of ongoing uploads.
   *
   * @param value The new number of ongoing uploads.
   */
  setOnGoingUploads(value: number): void {
    this.onGoingUploads = value;
    this.runResolvers();
  }

  /**
   * @returns The current number of ongoing uploads.
   */
  getOnGoingUploads(): number {
    return this.onGoingUploads;
  }

  /**
   * Executes all pending resolvers for the upload process and tile parent canvas generation.
   * This method triggers the resolution of promises associated with starting the upload process
   * and generating tiles on the parent canvas.
   */
  runResolvers(): void {
    this.startUploadProcess.resolver?.();
    this.tilesParentCanvasGeneration.resolver?.();
  }

  /**
   * Adds tile upload statistics to the list of statistics.
   *
   * @param stats - The tiles upload statistics to add to the collection.
   */
  addStats(stats: TilesUploadStats): void {
    this.tilesUploadStatsList.push(stats);
  }

  /**
   * Removes a TilesUploadStats object from the tiles upload statistics list.
   *
   * @param stats - The TilesUploadStats object to remove from the list.
   * @throws {Error} Throws an error if the stats object is not found in the list.
   */
  removeStats(stats: TilesUploadStats): void {
    const index = this.tilesUploadStatsList.indexOf(stats);
    assert(index !== -1, "value not found in the list of stats.");
    this.tilesUploadStatsList.splice(index, 1);
  }
}
